// ---------------------------------------------------------------------------
// Tedmo Services — editable site configuration
// Update the values below to personalize the site (company name, contact
// details, social links, product availability). Product `url` values are
// optional: leave "" to show only the availability label.
// ---------------------------------------------------------------------------

export const SITE = {
  companyName: "Tedmo Services",
  tagline: "Software & Cloud Infrastructure",
  domain: "https://tedmoservices.net",
  // Placeholder contact details — replace with your real inbox/phone.
  email: "admin@tedmoservices.com",
  phone: "",
  // Placeholder social links — paste real profile URLs to show the icons.
  socials: {
    linkedin: "",
    github: "",
    x: "",
  },
  products: {
    reelcopy: {
      name: "ReelCopy AI",
      category: "AI Video Archiving",
      status: "Inquire for Access",
      url: "",
      description:
        "A video platform for saving, organizing, and archiving supported online videos, with AI tools for caption and text cleanup. Designed for content users own or have permission to use.",
      highlights: ["Save & organize videos", "AI caption cleanup", "Permission-first archiving"],
    },
    eduhub: {
      name: "EduHub AI",
      category: "STEM & Literacy",
      status: "Inquire for Access",
      url: "",
      description:
        "An education platform supporting STEM and literacy for K–12 learners worldwide. AI-powered tools help teachers prepare lessons and deliver instruction more efficiently.",
      highlights: ["K–12 STEM & literacy", "AI lesson preparation", "Teacher-first tooling"],
    },
    proxavi: {
      name: "Proxavi",
      category: "Advanced Health AI",
      status: "Inquire for Access",
      url: "",
      description:
        "A multi-vertical operating system for regulated care in the US — home health, hospice, mental health, group and assisted living. Built for HIPAA workflows, mandatory MFA, and BAA-first onboarding, with the CareIQ multi-model AI copilot in every workflow.",
      highlights: ["HIPAA-ready workflows", "Mandatory MFA · BAA-first", "CareIQ AI copilot"],
    },
  },
};

// Display order for product listings. Keys must match ALLOWED_PRODUCTS in
// backend/server.py (used by the "Notify me" waitlist endpoint).
export const PRODUCT_KEYS = ["reelcopy", "eduhub", "proxavi"];

export const IMAGES = {
  hero: "https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  serviceApp:
    "https://images.unsplash.com/photo-1763718528755-4bca23f82ac3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxzb2Z0d2FyZSUyMGRhc2hib2FyZCUyMHVpJTIwd2lyZWZyYW1lfGVufDB8fHx8MTc5MjI5NzUzMHww&ixlib=rb-4.1.0&q=85",
  serviceCloud:
    "https://images.unsplash.com/photo-1667984390538-3dea7a3fe33d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxjbG91ZCUyMHNlcnZlciUyMGRhdGFjZW50ZXJ8ZW58MHx8fHwxNzkyMjk3NTMwfDA&ixlib=rb-4.1.0&q=85",
  serviceServer:
    "https://images.unsplash.com/photo-1506399558188-acca6f8cbf41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxjbG91ZCUyMHNlcnZlciUyMGRhdGFjZW50ZXJ8ZW58MHx8fHwxNzkyMjk3NTMwfDA&ixlib=rb-4.1.0&q=85",
  serviceDevops:
    "https://images.pexels.com/photos/4816921/pexels-photo-4816921.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  serviceSecurity:
    "https://images.pexels.com/photos/17489160/pexels-photo-17489160.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  serviceSupport:
    "https://images.pexels.com/photos/39071423/pexels-photo-39071423.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  serviceAi:
    "https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwbmV1cmFsJTIwbmV0d29yayUyMGFic3RyYWN0fGVufDB8fHx8MTc5MjMwMjY1OXww&ixlib=rb-4.1.0&q=85",
  reelcopy:
    "https://images.pexels.com/photos/27141316/pexels-photo-27141316.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  eduhub:
    "https://images.pexels.com/photos/5306480/pexels-photo-5306480.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  proxavi:
    "https://images.unsplash.com/photo-1666886573531-48d2e3c2b684?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxoZWFsdGhjYXJlJTIwdGVjaG5vbG9neSUyMHRhYmxldCUyMG51cnNlfGVufDB8fHx8MTc5MjMwMDI4N3ww&ixlib=rb-4.1.0&q=85",
};

// Must stay in sync with ALLOWED_SERVICES in backend/server.py.
export const SERVICE_OPTIONS = [
  "Application Development",
  "AI Applications & Capabilities",
  "Cloud Infrastructure (AWS/Azure/GCP)",
  "Server & VM Deployment",
  "DevOps & Automation",
  "Security & Reliability",
  "Ongoing Support",
  "AI Products (ReelCopy / EduHub / Proxavi)",
  "Other / Not Sure",
];

export const SERVICES = [
  {
    slug: "application-development",
    name: "Application Development",
    tag: "Web Platforms & APIs",
    image: IMAGES.serviceApp,
    imageAlt: "Modern application interface wireframes and API backend",
    summary:
      "Custom web applications, business platforms, APIs, and seamless integrations.",
    problem:
      "Off-the-shelf software rarely fits the way your business actually runs.",
    detail:
      "We design and build custom web applications, internal business platforms, APIs, and integrations between the tools you already use — so your software matches your processes instead of the other way around.",
    outcomes: [
      "Custom web applications",
      "Business platforms & dashboards",
      "APIs & third-party integrations",
    ],
  },
  {
    slug: "ai-applications",
    name: "AI Applications & Capabilities",
    tag: "LLMs · Copilots · Automation",
    image: IMAGES.serviceAi,
    imageAlt: "Abstract neural network visualization representing AI systems",
    summary:
      "Build and deploy AI-powered apps, copilots, and automations — grounded in your own data and running on infrastructure you control.",
    problem:
      "Everyone is told to 'add AI', but turning a demo into a secure, reliable product is where most projects stall.",
    detail:
      "We design, build, and deploy AI applications and capabilities for your business: LLM-powered assistants and copilots, document and data intelligence, retrieval over your own knowledge base, and workflow automation — integrated with your existing systems, deployed to the cloud or your own servers, and monitored like any other production system.",
    outcomes: [
      "AI assistants & copilots",
      "Document & data intelligence (RAG)",
      "AI workflow automation",
      "AI voice assistants",
      "Model integration & deployment",
    ],
  },
  {
    slug: "cloud-infrastructure",
    name: "Cloud Infrastructure",
    tag: "AWS · Azure · GCP",
    image: IMAGES.serviceCloud,
    imageAlt: "Multi-region cloud cluster deployment architecture",
    summary:
      "Architecture, setup, migration, and management across AWS, Azure, and Google Cloud.",
    problem:
      "Moving to the cloud without a plan leads to downtime, sprawl, and surprise bills.",
    detail:
      "We architect, set up, migrate, and manage your workloads across AWS, Microsoft Azure, and Google Cloud — sized for your traffic today and ready to scale tomorrow.",
    outcomes: [
      "Cloud architecture & setup",
      "Migration with minimal disruption",
      "Cost & performance management",
    ],
  },
  {
    slug: "server-deployment",
    name: "Server Deployment",
    tag: "Dedicated & VMs",
    image: IMAGES.serviceServer,
    imageAlt: "Enterprise server hardware and virtual machine infrastructure",
    summary:
      "Dedicated servers, virtual machines, and on-premises server environments.",
    problem:
      "Some workloads belong on infrastructure you control — not a shared platform.",
    detail:
      "We deploy and host your applications on dedicated servers, virtual machines, or on-premises infrastructure, configured for security, reliability, and easy maintenance.",
    outcomes: [
      "Dedicated server hosting",
      "Virtual machine environments",
      "On-premises deployments",
    ],
  },
  {
    slug: "devops-automation",
    name: "DevOps & Automation",
    tag: "CI/CD · IaC · Kubernetes",
    image: IMAGES.serviceDevops,
    imageAlt: "Automated deployment pipeline code on a developer screen",
    summary:
      "CI/CD pipelines, Infrastructure as Code, Docker containers, and Kubernetes orchestration.",
    problem: "Manual releases are slow, error-prone, and hold your team back.",
    detail:
      "We build CI/CD pipelines, manage infrastructure as code, and containerize with Docker and Kubernetes — so shipping an update becomes routine instead of risky.",
    outcomes: ["CI/CD pipelines", "Infrastructure as Code", "Docker & Kubernetes"],
  },
  {
    slug: "security-reliability",
    name: "Security & Reliability",
    tag: "Monitoring · Backups · DR",
    image: IMAGES.serviceSecurity,
    imageAlt: "Illuminated server racks in a secured data center",
    summary:
      "Secure configurations, continuous monitoring, automated backups, and disaster recovery.",
    problem: "An outage or a breach costs more than prevention ever will.",
    detail:
      "We harden configurations, monitor your systems, automate backups, and prepare disaster recovery plans — plus performance optimization to keep everything fast.",
    outcomes: [
      "Secure configuration",
      "Monitoring & alerting",
      "Backups & disaster recovery",
    ],
  },
  {
    slug: "ongoing-support",
    name: "Ongoing Support",
    tag: "Maintenance & Updates",
    image: IMAGES.serviceSupport,
    imageAlt: "Operations engineer monitoring systems across multiple screens",
    summary:
      "System maintenance, infrastructure troubleshooting, proactive updates, and optimization.",
    problem: "Software that isn't maintained quietly becomes a liability.",
    detail:
      "We stay after launch: application maintenance, infrastructure management, troubleshooting, and regular updates — one accountable team that knows your systems.",
    outcomes: [
      "Application maintenance",
      "Infrastructure management",
      "Troubleshooting & updates",
    ],
  },
];

export const PROCESS_STEPS = [
  {
    name: "Discovery",
    summary: "Scope system requirements and tech constraints.",
    detail:
      "We learn how your business works, what you need the software to do, and the constraints around budget, timeline, and existing systems.",
    deliverable: "A clear scope and technical requirements.",
  },
  {
    name: "Design",
    summary: "Design application architecture and cloud map.",
    detail:
      "We design the application architecture and the infrastructure it will run on — cloud, dedicated servers, or a mix of both.",
    deliverable: "An architecture and deployment plan you approve before we build.",
  },
  {
    name: "Build",
    summary: "Develop custom codebase with integrated tests.",
    detail:
      "We develop your application in iterative milestones, with testing integrated from the start, and keep you updated as it takes shape.",
    deliverable: "Working software you can review as it grows.",
  },
  {
    name: "Deploy",
    summary: "Deploy to cloud clusters or private servers.",
    detail:
      "We deploy to AWS, Azure, Google Cloud, dedicated servers, or your own on-premises environment — configured, secured, and documented.",
    deliverable: "A production-ready launch on the right infrastructure.",
  },
  {
    name: "Support",
    summary: "Ongoing maintenance, updates, and monitoring.",
    detail:
      "We stay accountable after launch: monitoring, maintenance, troubleshooting, and updates as your business grows.",
    deliverable: "A long-term team that knows your systems.",
  },
];

// NOTE: this FAQ copy is mirrored in the FAQPage JSON-LD in public/index.html.
// If you edit it here, update the JSON-LD to match exactly.
export const FAQ_ITEMS = [
  {
    question: "Who does Tedmo Services work with?",
    answer:
      "We primarily work with small and mid-sized businesses, and we also support companies modernizing legacy systems and startups launching their first product.",
  },
  {
    question: "Where does Tedmo Services operate?",
    answer:
      "We work remotely with businesses worldwide, delivering application development, cloud deployment, and infrastructure support wherever you operate.",
  },
  {
    question: "What happens after I request a consultation?",
    answer:
      "We review your project description and get in touch to discuss your project. The form does not book a time directly.",
  },
  {
    question: "Do you support applications after launch?",
    answer:
      "Yes. We handle the full journey, from building your application to deploying it on cloud platforms or your own servers, with security built into the process and ongoing support after launch.",
  },
];
